import React, {useEffect, useMemo, useRef, useState} from 'react';

import {useNavigate} from 'react-router-dom';

import {IconNotes, IconChevronRight, IconPaperclip, IconHome, IconFolder, IconPlus, IconStar} from '@tabler/icons-react';

import {Group, ScrollArea, Text, UnstyledButton, Box, ThemeIcon, Collapse} from '@mantine/core';

import './sidebar.css';

import {WorkspaceSelector} from '../workspace_selector';
import {useModals} from '../../modals/modals_provider';
import {useCurrentWorkspaceId} from '@/store/modules/workspaces_module';
import {useAction, useActions, useModule, useSelectorWithRefresh} from '@/store/kysely_module';
import {getIsTauriWebview, useIsMobile} from '@/utils/user_agent';
import {navigateToPathForReason} from '@/modules/navigation/navigation_module';

type Props = {
    closeSidebar: () => void;
}

const isTauriWebview = getIsTauriWebview();

export function Sidebar(props: Props) {
    const modals = useModals();

    const isMobile = useIsMobile();

    const [isFirstRender, setIsFirstRender] = useState(true);

    useEffect(() => {
        if (isFirstRender) {
            setTimeout(() => {
                setIsFirstRender(false);
            }, 100);
        }
    }, [isFirstRender, setIsFirstRender]);

    const workspaceId = useCurrentWorkspaceId();
    const sidebarComponents = useModule('UIEntrypoints').sidebar.components;

    const [starredObjects, refreshStarredObjects] = useSelectorWithRefresh('getAllMyStars', {}, {files: [], projects: []}, {workspace_id: null});
    const [workspaces] = useSelectorWithRefresh('getAllWorkspaces', {}, [], {workspace_id: null});
    const currentWorkspace = workspaces.find(workspace => workspace.id === workspaceId);

    const navigate = useNavigate();
    const actions = {
        createProject: useAction('createProject'),
    };

    // useEffect(() => {
    //     refreshStarredObjects();
    // }, [workspaceId]);

    const sidebarData = useMemo(() => [
        isTauriWebview && (process.env.PUBLIC_SHOW_LOCAL_DIRECTORIES_IN_SIDEBAR === 'true') && {
            label: 'Local Directories',
            icon: IconFolder,
            link: '/modules/LocalDesktopFiles',
        },
        {
            label: 'Starred Projects',
            icon: IconStar,
            // actions: [
            //     {
            //         id: 'new-project',
            //         label: 'New Project',
            //         icon: IconPlus,
            //         action: () => {
            //             modals.openModal('TextInputModal', {
            //                 title: `New project in "${currentWorkspace?.name || ''}"`,
            //                 inputName: 'Project name',
            //                 onSubmit: async (projectName) => {
            //                     const project = await actions.createProject({name: projectName, workspace_id: workspaceId}, {workspace_id: workspaceId});
            //                     navigateToPathForReason(navigate, 'navigate_to_project_page_from_sidebar_create_project_success', `/workspaces/${workspaceId}/projects/${project.id}`);
            //                     props.closeSidebar();
            //                     refreshStarredObjects();
            //                 },
            //             });
            //         },
            //     },
            // ],
            links: starredObjects.files.map(file => ({
                id: file.id,
                label: file.name,
                link: `/workspaces/${workspaceId}/files/${file.id}`,
            })),
        },
    ].filter(Boolean), [starredObjects]);

    const links = sidebarData.map((item) => item && (
        <LinksGroup
            {...item}
            onClick={() => {
                // if (item.onClick) {
                //     item.onClick(modals);
                // }
            }}
            key={item.label}
            closeSidebar={props.closeSidebar}
        />
    ));

    if (isFirstRender && isMobile) {
        return null;
    }

    return (
        <nav className={'sidebar'}>
            {/* <div className={'header'}>
                <Group justify='space-between'>
                    <WorkspaceSelector
                        closeSidebar={props.closeSidebar}
                    />
                </Group>
            </div> */}

            <ScrollArea className={'links'}>
                <div className={'linksInner'}>{links}</div>
            </ScrollArea>

            <div className={'footer'}>
                {/* <UserButton /> */}
            </div>
        </nav>
    );
}

interface LinksGroupProps {
    icon: React.FC<any>;
    label: React.ReactNode;
    initiallyOpened?: boolean;
    links?: {label: string; id: string; link: string}[];
    actions?: {label: string; id: string; action: () => void, icon: React.FC<any>}[];
    link?: string;
    onClick?: () => void;
    closeSidebar: () => void;
}

function LinksGroup({icon: Icon, label, initiallyOpened, links, actions, link, closeSidebar, onClick}: LinksGroupProps) {
    const navigate = useNavigate();

    const hasLinks = Array.isArray(links);
    const [opened, setOpened] = useState(initiallyOpened || false);
    const items = (hasLinks ? links : []).map((link) => (
        <Text
            component='a'
            className={'sidebar-link sidebar-nested-item'}
            href={link.link}
            key={link.id}
            onClick={(event) => {
                event.preventDefault();
                closeSidebar();

                if (link.link) {
                    navigateToPathForReason(navigate, 'navigate_to_dynamic_path_from_sidebar', link.link);
                }
            }}
        >
            {link.label}
        </Text>
    ));

    const hasActions = Array.isArray(actions);
    const actionItems = (hasActions ? actions : []).map((action) => (
        <div className={'sidebar-action sidebar-nested-item'} key={action.id}>
            <Box style={{display: 'flex', alignItems: 'center'}}>
                <ThemeIcon variant='light' size={30}>
                    <action.icon size={18} />
                </ThemeIcon>
                <span
                    onClick={(event) => {
                        event.preventDefault();
                        closeSidebar();

                        if (action.action) {
                            action.action();
                        }
                    }}
                >
                    {action.label}
                </span>
            </Box>
        </div>
    ));

    const onClickToplevelLink = (e: React.MouseEvent) => {
        e.preventDefault();
        if (hasLinks) {
            setOpened((o) => !o)
        } else if (link) {
            navigateToPathForReason(navigate, 'navigate_to_dynamic_path_from_sidebar', link);
            closeSidebar();
        } else if (onClick) {
            onClick();
            closeSidebar();
        }
    };

    return (
        <>
            <UnstyledButton
                component='a'
                href={link}
                className={'control'}
                onClick={onClickToplevelLink}
            >
                <Group justify='space-between' gap={0}>
                    <Box style={{display: 'flex', alignItems: 'center'}}>
                        <ThemeIcon variant='light' size={30}>
                            <Icon size={18} />
                        </ThemeIcon>
                        <Box ml='md'>{label}</Box>
                    </Box>
                    {(hasLinks || hasActions) && (
                        <IconChevronRight
                            className={'chevron'}
                            stroke={1.5}
                            size={16}
                            style={{transform: opened ? 'rotate(-90deg)' : 'none'}} />
                    )}
                </Group>
            </UnstyledButton>
            {(hasLinks || hasActions) ? (
                <Collapse in={opened}>
                    {hasActions && actionItems}
                    {hasLinks && items}
                </Collapse>
            ) : null}
        </>
    );
}
